import React from 'react';
import { AxiosResponse, AxiosError } from 'axios';
import { CityDirectoryData } from 'types/app';
import { CityDirectory } from '../../containers/CityDirectory';
import { ErrorPage } from '../../containers/ErrorPage';
import { ENDPOINT_CITY_DIRECTORY_GET } from '../../constants/endpoints';
import { HttpHeaderKey } from '../../constants/http-header-key';
import { DEFAULT_CACHE_POLICY } from '../../constants/cache';
import { GetCityDirectoryResponse, getCityDirectoryTransformer } from '../../transformers/get-city-directory-transformer';
import { axiosContent } from '../../utils/axios';
import { requestHandler } from '../../utils/requestHandler';
import { tryCatch } from '../../utils/tryCatch';
import { isNotFoundException } from '../../utils/api/isNotFoundException';
import { NotFoundException } from '../../utils/exceptions/NotFoundException';
import { isRedirectException } from '../../utils/api/isRedirectException';
import { Headers, getRedirectLocation } from '../../utils/api/getRedirectLocation';
import { RedirectException } from '../../utils/exceptions/RedirectException';
import { httpException } from '../../utils/exceptions/http.exception';
type Props = {
  data: CityDirectoryData;
  statusCode: number;
};
const CityDirectoryPage = ({
  data,
  statusCode
}: Props) => {
  // Next.js resolves the URL (and thus a 200) to this page, but the wagcontent API could
  // potentially return a 404 due to not finding the city/state given in the endpoint
  // (or a 500 due to a server error). In these cases, we should actually return an error
  // page to the front-end.
  if (statusCode === 404 || statusCode >= 500) {
    return <ErrorPage statusCode={statusCode} message="" />;
  }
  return <CityDirectory data={data} data-sentry-element="CityDirectory" data-sentry-component="CityDirectoryPage" data-sentry-source-file="[city].tsx" />;
};
const handleGetServerSideProps = requestHandler({
  headers: {
    [HttpHeaderKey.CacheControl]: DEFAULT_CACHE_POLICY
  }
});
export const getServerSideProps = handleGetServerSideProps(async ({
  query
}) => {
  try {
    const {
      slug: state,
      city
    } = query;
    const STATE_ABBREV_LENGTH = 2;
    if (state && state.length !== STATE_ABBREV_LENGTH) {
      return {
        notFound: true
      };
    }
    const [response, error] = await tryCatch<AxiosResponse<GetCityDirectoryResponse>, AxiosError>(() => axiosContent.get(ENDPOINT_CITY_DIRECTORY_GET({
      state,
      city
    })));
    if (error) {
      const statusCode = error.response.status;
      if (isNotFoundException(statusCode)) {
        return {
          notFound: true
        };
      }
      if (isRedirectException(statusCode)) {
        const redirectLocation = getRedirectLocation(error.response.headers as Headers);
        return {
          redirect: {
            destination: redirectLocation,
            permanent: false
          }
        };
      }
      return {
        props: {
          data: null,
          statusCode
        }
      };
    }
    const transformedData = getCityDirectoryTransformer(response.data);
    return {
      props: {
        data: {
          ...transformedData,
          stateAbbrev: state
        },
        statusCode: 200
      }
    };
  } catch (error) {
    console.error('Error in getServerSideProps:', error);
    return {
      props: {
        data: null,
        statusCode: 500
      }
    };
  }
});
export default CityDirectoryPage;